import { useCallback, useRef, useState } from 'react'

/**
 * Per-action busy/feedback tracking, keyed so independent buttons each get
 * their own in-flight flag instead of blocking one another. Shared by the
 * B4 bot-control buttons (`useBotActions`), the B6 admin pages
 * (`useAdminActions`) and the B5 payment pages (`usePaymentActions`) — each
 * only supplies its own error wording (`describe`).
 *
 * The `inFlight` ref is checked and updated SYNCHRONOUSLY, before the first
 * `await` — a second click on the same key within the same tick (before
 * React has re-rendered the `disabled` state) is a no-op at that point, not
 * just visually blocked. That is the actual double-submit guard; disabling
 * the button is the visible half of it.
 */
export function useKeyedActions<F>(describe: (err: unknown) => F) {
  const [busyKeys, setBusyKeys] = useState<ReadonlySet<string>>(new Set())
  const [feedback, setFeedback] = useState<Record<string, F | undefined>>({})
  const inFlight = useRef<Set<string>>(new Set())
  const describeRef = useRef(describe)
  describeRef.current = describe

  const run = useCallback(async <T,>(key: string, action: () => Promise<T>, onSuccess?: (result: T) => void, onError?: (err: unknown) => void) => {
    if (inFlight.current.has(key)) return
    inFlight.current.add(key)
    setBusyKeys(new Set(inFlight.current))
    setFeedback((prev) => ({ ...prev, [key]: undefined }))
    try {
      const result = await action()
      onSuccess?.(result)
    } catch (err) {
      setFeedback((prev) => ({ ...prev, [key]: describeRef.current(err) }))
      onError?.(err)
    } finally {
      inFlight.current.delete(key)
      setBusyKeys(new Set(inFlight.current))
    }
  }, [])

  const clearFeedback = useCallback((key: string) => {
    setFeedback((prev) => (prev[key] === undefined ? prev : { ...prev, [key]: undefined }))
  }, [])

  /** Lets a caller post a SUCCESS-path message through the same per-key
   * channel the error path uses, so every outcome renders the same way. */
  const setFeedbackFor = useCallback((key: string, value: F | undefined) => {
    setFeedback((prev) => ({ ...prev, [key]: value }))
  }, [])

  return {
    isBusy: (key: string) => busyKeys.has(key),
    feedbackFor: (key: string) => feedback[key],
    run,
    clearFeedback,
    setFeedback: setFeedbackFor,
  }
}
